import camelCaseAttrMap from 'html-to-react/lib/camel-case-attribute-names';
import { uniqueId } from 'lodash';

/** @typedef {import('domhandler').Element} Element */

/**
 * @param {string} name
 * @returns {string}
 */
export function toReactProp(name) {
  name = camelCaseAttrMap[name] || name;

  switch (name) {
    case 'accept-charset':
      return 'acceptCharset';
    case 'class':
      return 'className';
    case 'for':
      return 'htmlFor';
    case 'value':
      return 'defaultValue';
    default:
      return name;
  }
}

/**
 * @param {string} inlineCss
 * @returns {React.CSSProperties}
 */
export function convertCssToReactStyle(inlineCss) {
  return inlineCss
    .split(';')
    .map((cur) => cur.split(':'))
    .reduce((acc, [key, value]) => {
      if (!key.startsWith('--')) {
        key = key.replace(/-./g, (css) => css[1].toUpperCase());
      }

      acc[key] = value;
      return acc;
    }, {});
}

/*
  some buttons have handlers like click="this.closest('cart-drawer').close()"
  we need to execute them with this = currentElement
  To do this, I find the corresponding element by ID and use it instead of "this"
*/
function toElementFunction(value, swellId) {
  return () => {
    const _swellElement = document.querySelector(
      `[data-swell-element-id="${swellId}"]`,
    );
    if (_swellElement) {
      eval(value.replaceAll('this.', '_swellElement.'));
    }
  };
}

function toFunction(value) {
  if (typeof value === 'function') {
    return value;
  }

  // eslint-disable-next-line no-new-func
  return Function(value);
}

/**
 * @param {Element} node
 * @param {boolean} [parseStyles]
 */
export function replaceProps(node, parseStyles = true, replaceThis = false) {
  if (!node?.attribs) {
    return;
  }

  if (typeof node.attribs.style === 'string' && parseStyles) {
    node.attribs.style = convertCssToReactStyle(node.attribs.style);
  }

  const swellId = uniqueId();

  // Replace props for react components
  const entires = Object.entries(node.attribs);
  for (const [attr, value] of entires) {
    const reactAttr = toReactProp(attr);

    if (reactAttr !== attr) {
      delete node.attribs[attr];

      if (attr.startsWith('on')) {
        if (
          replaceThis &&
          typeof value === 'string' &&
          value.includes('this.')
        ) {
          node.attribs[reactAttr] = toElementFunction(value, swellId);
          // add attribute with unique id
          node.attribs['data-swell-element-id'] = swellId;
        } else {
          node.attribs[reactAttr] = toFunction(value);
        }
      } else {
        node.attribs[reactAttr] = value;
      }
    }
  }
}

/**
 * Checks whether a node is part of a layout section.
 *
 * This is used to avoid loading assets for layout sections,
 * as their assets are loaded by the `Section` component.
 *
 * @param {Element} node - The DOM node to check.
 * @returns {boolean} Returns `true` if the node is a layout section asset, otherwise `false`.
 */
export function isLayoutSectionAsset(node) {
  if (!node || node.name === 'body') {
    return false;
  }

  if (node.attribs?.id?.startsWith('swell-section-group')) {
    return true;
  }

  return isLayoutSectionAsset(node.parent);
}

/**
 * @param {Element} node
 * @param {[string, unknown][]} attrs
 */
export function applyAttributes(node, attrs) {
  for (const [attr, value] of attrs) {
    node.attribs[attr] = value;
  }
}
